import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useAuthState } from 'react-firebase-hooks/auth';
import { Table, Input, Button, Tag, Tooltip, Card, Space, Empty } from 'antd';
import { auth } from '../../firebase/firebase-config';
import BeatLoader from "react-spinners/BeatLoader";
import { ConfirmModal } from "../../components";
import { 
  FileAddOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ImportOutlined, 
  LinkOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { toast } from "react-toastify";
import { 
  getAllRadiologyExams, 
  deleteRadiologyExam 
} from "../../services/radiology_exam";
import AddRadiologyExamModal from "./AddRadiologyExamModal/AddRadiologyExamModal";
import ImportRadiologyExamsModal from "./ImportRadiologyExamsModal/ImportRadiologyExamsModal";
import AssignRadiologyExamModal from "./AssignRadiologyExamModal/AssignRadiologyExamModal";
import './RadiologyExamPage.css';

const { Search } = Input;

function RadiologyExamPage() {
  // State management
  const [allRadiologyExams, setAllRadiologyExams] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentActiveId, setCurrentActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Firebase auth
  const [user] = useAuthState(auth);
  
  // Delete radiology exam handlers
  const handleDelete = (id) => {
    setCurrentActiveId(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteRadiologyExam(currentActiveId);
      toast.success('Radiology exam deleted successfully');
      fetchData();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error deleting radiology exam:', error);
      toast.error('Error deleting radiology exam');
    }
  };

  // Edit radiology exam handler
  const handleEdit = (examData) => {
    setEditData(examData);
    setShowCreateModal(true);
  };

  // Assign radiology exam handler
  const handleAssign = (examId) => {
    setSelectedExamId(examId);
    setShowAssignModal(true);
  };

  // Modal toggles
  const toggleAddModal = () => {
    setEditData(null);
    setShowCreateModal(!showCreateModal);
  };

  const toggleImportModal = () => {
    setShowImportModal(!showImportModal);
  };

  // Fetch radiology exams data
  const fetchData = async () => {
    setLoading(true);
    setFilteredData([]); // Clear data while loading
    
    try {
      const response = await getAllRadiologyExams();
      
      // Handle different response structures
      let exams = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          exams = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          exams = response.data.data;
        }
      }
      
      // Add index property for display
      const examsWithIndex = exams.map((exam, index) => ({
        ...exam,
        index: index + 1
      }));
      
      setAllRadiologyExams(examsWithIndex);
    } catch (error) {
      console.error('Error fetching radiology exams:', error);
      toast.error('Error fetching radiology exams');
      setAllRadiologyExams([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Filter data when exams or search change
  useEffect(() => {
    const dataToFilter = Array.isArray(allRadiologyExams) ? allRadiologyExams : [];
    const filtered = filterData(dataToFilter, searchText);
    setFilteredData(filtered);
  }, [allRadiologyExams, searchText]);

  // Search filter function
  const filterData = (data, query) => {
    if (!Array.isArray(data)) return [];
    if (!query) return data;

    return data.filter((item) =>
      Object.values(item || {}).some((value) =>
        value?.toString().toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  // Search handler
  const handleSearch = (value) => {
    setSearchText(value);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchText('');
  };

  // Format price range for display
  const formatPrice = (minPrice, maxPrice) => {
    if (!minPrice && !maxPrice) return 'Not specified';
    if (minPrice && !maxPrice) return `${minPrice} FCFA`;
    if (!minPrice && maxPrice) return `Up to ${maxPrice} FCFA`;
    return (
      <div className="price-range">
        <span>{minPrice} FCFA</span>
        <span className="price-dash">-</span>
        <span>{maxPrice} FCFA</span>
      </div>
    );
  };

  // Get total exam count
  const getExamCount = () => {
    return Array.isArray(allRadiologyExams) ? allRadiologyExams.length : 0;
  };

  // Table columns definition
  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => {
        if (!a.category || !b.category) return 0;
        return a.category.localeCompare(b.category);
      },
      render: (text, record) => (
        <div className="category-cell">
          <span className="category-text">{text || 'Uncategorized'}</span>
          {record.categoryFr && (
            <span className="category-text-fr">{record.categoryFr}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Exam Name',
      dataIndex: 'examName',
      key: 'examName',
      sorter: (a, b) => {
        if (!a.examName || !b.examName) return 0;
        return a.examName.localeCompare(b.examName);
      },
      render: (text) => (
        <div className="exam-name-cell">
          <span className="exam-name">{text || 'No name'}</span>
        </div>
      ),
    },
    {
      title: 'Price Range (FCFA)',
      key: 'price',
      sorter: (a, b) => {
        const minPriceA = parseFloat(a.minPrice) || 0;
        const minPriceB = parseFloat(b.minPrice) || 0;
        return minPriceA - minPriceB;
      },
      render: (_, record) => formatPrice(record.minPrice, record.maxPrice),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit Exam">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Assign to Facility">
            <Button 
              type="text" 
              icon={<LinkOutlined />} 
              onClick={() => handleAssign(record.id)} 
              className="action-button" 
            />
          </Tooltip>
          <Tooltip title="Delete Exam">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record.id)} 
              className="action-button" 
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Expanded row render function
  const expandedRowRender = (record) => {
    return (
      <div className="expanded-row">
        {record.description && (
          <div className="expanded-section">
            <h4>Description</h4>
            <p>{record.description}</p>
            {record.descriptionFr && (
              <p className="secondary-text">{record.descriptionFr}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Layout title="Radiology Exams" pageTitle="All Radiology Exams">
        <div className="section radiologyexam-page">
          {/* Summary card */}
          <div className="summary-cards">
            <Card className="summary-card">
              <div className="card-icon">
                <ExperimentOutlined />
              </div>
              <div className="card-content">
                <h3>{loading ? <BeatLoader size={8} color="#322659" /> : getExamCount()}</h3>
                <p>Total Radiology Exams</p>
              </div>
            </Card>
          </div>
          
          {/* Main content */}
          <div className="data-table">
            {/* Header */}
            <div className="top">
              <div className="span">
                <h3>Radiology Exams Directory</h3>
              </div>
              <div className="action-group">
                <Button
                  type="primary"
                  icon={<ImportOutlined />}
                  onClick={toggleImportModal}
                  className="btn-import"
                >
                  Import
                </Button>
                <Button
                  type="primary"
                  icon={<FileAddOutlined />}
                  onClick={toggleAddModal}
                  className="btn-add"
                >
                  Add Exam
                </Button>
              </div>
            </div>
            
            {/* Search and filters */}
            <div className="filter-section">
              <Search
                placeholder="Search radiology exams..."
                allowClear
                onSearch={handleSearch}
                onChange={(e) => handleSearch(e.target.value)}
                value={searchText}
                prefix={<SearchOutlined />}
              />
              
              <Space>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData}
                >
                  Refresh
                </Button>
              </Space>
            </div>
            
            {/* Table */}
            <div className="table-container">
              {loading ? (
                <div className="loading-container">
                  <BeatLoader color="#322659" />
                  <p className="loading-message">Loading radiology exam data...</p>
                </div>
              ) : filteredData.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={filteredData}
                  rowKey={(record) => record.id || Math.random().toString()}
                  expandable={{
                    expandedRowRender,
                    expandRowByClick: true,
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} exams`
                  }}
                  className="radiologyexams-table"
                />
              ) : (
                <Empty 
                  description={
                    searchText ? "No radiology exams match your search" : "No radiology exams found"
                  } 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </div>
        </div>
      </Layout>

      {/* Modals */}
      {showCreateModal && (
        <AddRadiologyExamModal
          editData={editData}
          onClose={() => {
            setShowCreateModal(false);
            setEditData(null);
          }}
          onContentAdded={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}

      {showImportModal && (
        <ImportRadiologyExamsModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={() => {
            setShowImportModal(false);
            fetchData();
          }}
        />
      )}

      {showAssignModal && (
        <AssignRadiologyExamModal
          radiologyExamId={selectedExamId}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedExamId(null);
          }}
          onAssignComplete={() => {
            setShowAssignModal(false);
            toast.success('Radiology exam assigned successfully');
          }}
        />
      )}
      
      {showConfirmModal && (
        <ConfirmModal
          title="Are you sure you want to delete this radiology exam?"
          onAccept={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </>
  );
}

export default RadiologyExamPage;